/*
 * biobio-bundle-selector.js
 *
 * Custom Web Component <bundle-selector> que gerencia a UI de bundle
 * com variantes (Shopify Bundles app oficial). Renderizado como block
 * dentro do main-product section.
 *
 * Spec: ../../ui-ux/04-design-system/componentes/bundle-selector/handover-liquid.md
 * Modo de campanha (kits vendidos como itens avulsos): docs/specs/2026-10-08-kits-avulsos-10-10-design.md
 */

class BundleSelector extends HTMLElement {
  // RECONEXAO SEGURA (LP do Trio, Tarefa 7 do plano
  // docs/plans/2026-08-11-lp-trio-essencial.md).
  //
  // Mover um custom element no DOM (o portal que leva ESTE no pra dentro do
  // drawer de compra da LP e o devolve depois) dispara disconnectedCallback
  // seguido de connectedCallback. Sem guarda, cada movimento:
  //   1. religava um listener de click em cada swatch e um de change em cada
  //      radio (bind() cria funcao NOVA a cada chamada, entao removeEventListener
  //      nem seria possivel) -> N cliques viravam N selecoes e, pior, o
  //      attachThemeAjaxHandler rodava de novo e o tema passava a ter DOIS
  //      handlers de submit no mesmo form: adicionar 1 vez colocaria 2 linhas
  //      no carrinho;
  //   2. recalculava selectedByComponent a partir dos radios (sobreviveria) mas
  //      zerava productCache e re-travava o CTA em "Selecione um tom", perdendo
  //      o estado que a cliente ja tinha montado;
  //   3. refazia o fetch de variantes.
  // Por isso o connectedCallback e IDEMPOTENTE por instancia: so faz setup na
  // primeira conexao. Comportamento na PDP (uma conexao unica) e byte-a-byte o
  // mesmo de antes.
  connectedCallback() {
    if (this._biobioSetupDone) return;
    this._biobioSetupDone = true;

    this.swatches = Array.from(this.querySelectorAll('.swatch'));
    this.radios = Array.from(this.querySelectorAll('.swatch input[type="radio"]'));
    this.variantInput = this.querySelector('[data-bundle-variant-id]');
    this.totalEl = this.querySelector('[data-bundle-total]');
    this.compareEl = this.querySelector('[data-bundle-compare]');
    this.discountEl = this.querySelector('[data-bundle-discount]');
    this.addToCartBtn = this.querySelector('[data-bundle-add-to-cart]');
    this.unavailableEl = this.querySelector('[data-bundle-unavailable]');

    // Price block nativo do tema (#price) — fica desabilitado no template de
    // bundle, entao essas refs normalmente sao null (no-op defensivo). Mantidas
    // pra resiliencia caso o merchant reative o block no Theme Editor.
    this.externalPriceEl = document.querySelector('#price .current-price');
    this.externalCompareEl = document.querySelector('#price .was-price');
    // Parcelamento + cashback consolidados DENTRO do CTA do combo (preco unico
    // na PDP de bundle — mesmo padrao do bundle-selector-fb).
    this.installmentEl = this.querySelector('[data-bundle-installment]');
    this.cashbackEl = this.querySelector('[data-bundle-cashback]');

    this.formatter = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });

    // Bind handler on BOTH the label click (works for mouse + touch on visually-hidden
    // radios, robust against tema overrides) AND the radio change (works for setinhas
    // teclado nativas que mudam checked sem disparar click no label).
    // Handler is idempotent — safe to fire twice for the same selection.
    for (const swatch of this.swatches) {
      swatch.addEventListener('click', this.handleSwatchActivate.bind(this));
    }
    for (const radio of this.radios) {
      radio.addEventListener('change', this.handleRadioChange.bind(this));
    }

    this.selectedByComponent = this.computeInitialSelection();

    this.abortController = new AbortController();
    this.productCache = null;

    // Modo de campanha (spec 2026-10-08, 4.3): kit listado em kits_avulsos adiciona os
    // componentes avulsos. So vale ate o fim gravado no atributo; pagina carregada depois
    // do fim roda no modo kit de sempre.
    const fimKitAvulso = Number(this.dataset.kitAvulsoFim || 0) * 1000;
    this.kitAvulsoFim = fimKitAvulso;
    this.kitAvulso = this.dataset.kitAvulso === '1' && fimKitAvulso > Date.now();
    // Texto original do aviso de indisponivel: o erro do add escreve a mensagem do carrinho no mesmo
    // elemento, e a proxima resolucao do modo devolve o texto de sempre (antes do loadProductData,
    // que resolve de forma sincrona com o JSON inline).
    this.unavailableOriginal = this.kitAvulso && this.unavailableEl ? this.unavailableEl.textContent : '';

    // Initial state: locked (incomplete selection). Sets both disabled
    // attribute AND --locked class for defense against 3rd-party scripts.
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.classList.add('bundle-cta__button--locked');
    }

    this.loadProductData();
    if (this.kitAvulso) {
      this.attachKitAvulsoHandler();
    } else {
      this.attachThemeAjaxHandler();
    }
  }

  // Plug into theme.applyAjaxToProductForm so the bundle's submit follows the
  // same flow as a regular product (AJAX /cart/add.js + open cart drawer).
  // The theme normally only inits .product-form--main on PDP load, so a
  // .product-form--bundle container needs an explicit hookup here.
  attachThemeAjaxHandler() {
    const productForm = this.querySelector('.product-form--bundle');
    if (!productForm) return;
    if (window.theme && typeof window.theme.applyAjaxToProductForm === 'function' && window.jQuery) {
      try {
        window.theme.applyAjaxToProductForm(window.jQuery(productForm));
      } catch (err) {
        console.warn('[bundle-selector] theme.applyAjaxToProductForm failed', err);
      }
    }
  }

  // Modo de campanha: o form nao vai para o gancho do tema (que postaria o id do kit).
  // A marca data-kit-avulso faz o recuo de page template (fim do arquivo) ignorar este form.
  // Nas PDPs o proprio tema liga o add AJAX no .product-form[data-ajax-add-to-cart]; sem a captura
  // aqui o mesmo clique postaria tambem o kit (aceite da T4 em 08/10). O listener fica em CAPTURA no
  // proprio seletor (ancestral do form): roda antes de qualquer handler ligado no form (o jQuery do
  // tema) e a propagacao parada aqui impede que o submit chegue neles. O tema liga so o submit do
  // form (theme.js, applyAjaxToProductForm), nao o click do botao.
  attachKitAvulsoHandler() {
    const form = this.querySelector('.bundle-cta__form');
    if (!form) return;
    form.setAttribute('data-kit-avulso', '1');
    this.addEventListener('submit', (event) => {
      if (event.target !== form) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.handleKitAvulsoSubmit(event);
    }, true);
  }
  // Um item por card: a variante do tom MARCADO no radio (a mesma fonte da resolucao do kit) ou, em
  // card sem tom, a do proprio card. A classe swatch--selected nao serve de fonte: so o JS a poe, e o
  // navegador que restaura o form (Back sem bfcache, reload do Firefox) devolve o radio marcado sem ela.
  // Card com tom e sem radio marcado, faltando variante ou preco: devolve null e o CTA fica travado;
  // nunca cai na primeira variante do componente.
  kitAvulsoItens() {
    const itens = [];
    for (const card of this.querySelectorAll('.bundle-card')) {
      const temTom = !!card.querySelector('.swatch input[type="radio"]');
      const marcado = card.querySelector('.swatch input[type="radio"]:checked');
      const sw = marcado && marcado.closest('.swatch[data-variant-id]');
      const id = Number(temTom ? (sw && sw.dataset.variantId) : card.dataset.componentVariantId);
      const price = Number(card.dataset.componentPrice);
      if (!id || !(price > 0)) return null;
      itens.push({ id, price });
    }
    return itens.length ? itens : null;
  }
  // A conta do 10.10: soma dos componentes com o mais barato a 50%. "De" e a soma cheia;
  // a nota da campanha entra no lugar do "-N%" do kit.
  mostrarContaKitAvulso(itens) {
    const soma = itens.reduce((s, i) => s + i.price, 0);
    const min = Math.min(...itens.map((i) => i.price));
    const total = soma - Math.round(min / 2);
    if (this.totalEl) this.totalEl.textContent = this.formatter.format(total / 100);
    if (this.compareEl) {
      this.compareEl.textContent = this.formatter.format(soma / 100);
      this.compareEl.hidden = false;
    }
    if (this.discountEl) {
      const nota = this.dataset.kitAvulsoNota;
      this.discountEl.textContent = nota || '';
      this.discountEl.hidden = !nota;
    }
    this.updateExternalPriceElements({ price: total, compare_at_price: soma });
    return total;
  }
  // Devolve ao aviso de indisponivel o texto original e o esconde (o fluxo de quem chama decide se
  // ele reaparece). So no modo de campanha.
  restaurarAvisoKitAvulso() {
    if (!this.kitAvulso || !this.unavailableEl) return;
    this.unavailableEl.textContent = this.unavailableOriginal;
    this.unavailableEl.hidden = true;
  }
  async handleKitAvulsoSubmit(event) {
    event.preventDefault();
    const btn = this.addToCartBtn;
    if (!btn || btn.disabled || btn.classList.contains('bundle-cta__button--locked') || btn.classList.contains('product-add--adding')) return;
    this.restaurarAvisoKitAvulso();
    const vencido = Date.now() >= this.kitAvulsoFim;
    const itens = vencido ? null : this.kitAvulsoItens();
    // Durante a campanha nunca cai no kit: sem os itens (tom sem marca, preco ou variante faltando),
    // o CTA volta a travado em vez de postar outra coisa.
    if (!vencido && !itens) {
      this.setCtaIncomplete();
      return;
    }
    let items;
    if (itens) {
      const kit = this.dataset.kitAvulsoHandle || this.dataset.productHandle;
      items = itens.map((i) => ({ id: i.id, quantity: 1, properties: { _origem: 'kit-avulso', _kit: kit } }));
    } else {
      const kitId = Number(this.variantInput && this.variantInput.value);
      if (!kitId) return;
      items = [{ id: kitId, quantity: 1 }];
    }
    btn.classList.add('product-add--adding');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || 'HTTP ' + res.status);
      }
      const t = window.theme;
      const cartPageMode = !!(t && t.settings && t.settings.cartType === 'page');
      if (t && typeof t.updateCartSummaries === 'function' && !cartPageMode) {
        t.updateCartSummaries(true, true, false);
        btn.classList.add('product-add--added');
        setTimeout(() => btn.classList.remove('product-add--added'), 2000);
      } else {
        window.location.href = '/cart';
      }
    } catch (err) {
      console.warn('[bundle-selector] add dos componentes avulsos falhou', err);
      if (this.unavailableEl) {
        this.unavailableEl.textContent = err.message;
        this.unavailableEl.hidden = false;
      }
    } finally {
      btn.classList.remove('product-add--adding');
    }
  }

  // Abort ADIADO pra uma microtask: um movimento sincrono dentro do documento
  // (portal do drawer da LP do Trio) desconecta e reconecta o no no MESMO tick,
  // entao cancelar o fetch aqui mataria o carregamento das variantes de um no
  // que continua vivo na pagina. A microtask roda depois do append de volta,
  // quando isConnected ja voltou a ser true. Remocao de verdade (no fora do
  // documento) continua abortando, so que um tick depois.
  disconnectedCallback() {
    queueMicrotask(() => {
      if (!this.isConnected) this.abortController?.abort();
    });
  }

  computeInitialSelection() {
    const map = {};
    for (const radio of this.radios) {
      if (!radio.checked) continue;
      const swatch = radio.closest('.swatch');
      if (swatch) map[swatch.dataset.componentIndex] = radio.value;
    }
    return map;
  }

  // Tenta carregar variantes inline primeiro (renderizado pelo Liquid via
  // <script type="application/json">) — sincrono, sem network. Fallback pra
  // fetch /products/handle.js se inline data ausente. Network failure mode
  // mostra erro user-visible em vez de travar o CTA silenciosamente.
  async loadProductData() {
    const inlineEl = this.querySelector('[data-bundle-product-variants]');
    if (inlineEl) {
      try {
        const variants = JSON.parse(inlineEl.textContent);
        this.productCache = { variants };
        this.resolveBundleVariant();
        return;
      } catch (err) {
        console.warn('[bundle-selector] inline variants parse failed, falling back to network', err);
      }
    }

    const handle = this.dataset.productHandle;
    try {
      const res = await fetch(`/products/${handle}.js`, {
        signal: this.abortController.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.productCache = await res.json();
      this.resolveBundleVariant();
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[bundle-selector] product fetch failed', err);
      this.handleFetchFailure();
    }
  }

  // Quando inline data ausente E fetch falha, surface erro pro user em vez
  // de travar silenciosamente o CTA com "Selecione um tom" pra sempre.
  handleFetchFailure() {
    if (this.unavailableEl) {
      this.unavailableEl.textContent = this.dataset.fetchError || 'Erro ao carregar variantes. Por favor recarregue a pagina.';
      this.unavailableEl.hidden = false;
    }
    // CTA mantem disabled (estado inicial)
  }

  handleSwatchActivate(e) {
    const swatch = e.currentTarget;
    if (swatch.classList.contains('swatch--disabled')) return;
    const radio = swatch.querySelector('input[type="radio"]');
    if (!radio || radio.disabled) return;
    // Force-toggle radio in case browser default action didn't fire.
    if (!radio.checked) radio.checked = true;
    this.applySelection(swatch, radio);
  }

  handleRadioChange(e) {
    const radio = e.currentTarget;
    const swatch = radio.closest('.swatch');
    if (!swatch) return;
    this.applySelection(swatch, radio);
  }

  applySelection(swatch, radio) {
    const idx = swatch.dataset.componentIndex;
    const card = swatch.closest('.bundle-card');

    if (card) {
      const cardSwatches = card.querySelectorAll('.swatch');
      for (const s of cardSwatches) {
        s.classList.remove('swatch--selected');
      }
      swatch.classList.add('swatch--selected');

      // Sallve-style pill: atualiza bullet color + nome da variante.
      // Pill bullet le --pill-bullet-bg CSS var, que setamos a partir do
      // --swatch-bg do label (cor hex configurada via metafield swatch.color).
      const variantName = radio.getAttribute('aria-label');
      const pill = card.querySelector('[data-bundle-pill]');
      const pillBullet = card.querySelector('[data-pill-bullet]');
      const pillName = card.querySelector('[data-pill-name]');
      if (pill) pill.removeAttribute('data-pill-empty');
      if (pillBullet) {
        // Espelha o swatch escolhido no bullet: cor E imagem (swatch nativo
        // do Shopify pode ser imagem — caso do BBS / Lip Oil).
        const swatchBg = swatch.style.getPropertyValue('--swatch-bg');
        const swatchImg = swatch.style.getPropertyValue('--swatch-image');
        if (swatchBg) pillBullet.style.setProperty('--pill-bullet-bg', swatchBg);
        if (swatchImg) {
          pillBullet.style.setProperty('--pill-bullet-image', swatchImg);
        } else {
          pillBullet.style.removeProperty('--pill-bullet-image');
        }
      }
      if (pillName && variantName) {
        // Strip sufixo " + <extra>" do nome no pill — ex: "#100 Avelã + BioRetinol"
        // vira "#100 Avelã". Convencao de naming: tudo antes de " + " e o tom/option
        // que o cliente escolheu; tudo depois sao bonus inclusos no kit (kept no
        // variant.title pro carrinho mostrar completo, mas espremido aqui pra UI ficar
        // limpa). No-op em variantes sem " + " (ex: combos Shopify Bundles com " / ").
        pillName.textContent = variantName.split(' + ')[0].trim();
      }
    }

    this.selectedByComponent[idx] = radio.value;
    this.resolveBundleVariant();

    this.dispatchEvent(new CustomEvent('bundle:variant-changed', {
      bubbles: true,
      detail: { selectedByComponent: { ...this.selectedByComponent } },
    }));
  }

  // Number of distinct radiogroups (one per component product with variants).
  // Used to detect "incomplete" selection state (user hasn't picked all yet).
  // Modo slots (data-bundle-slots, spec 2026-10-26 6.2): um tom por slot, mesmo
  // com o produto repetido entre slots (as duas Bases do combo).
  get expectedComponentCount() {
    if (this.dataset.bundleSlots) {
      return Number(this.dataset.bundleSlots);
    }
    const names = new Set();
    for (const r of this.radios) names.add(r.name);
    return names.size;
  }

  // Toggle disabled attribute AND .bundle-cta__button--locked class together.
  // 3rd-party scripts (Fast Bundle SDK, BOGOS, etc) sometimes call
  // $.removeAttr('disabled') on submit buttons inside .product-form when
  // they init. The class survives those calls because their selectors
  // target attr/property, not our custom class. CSS has !important +
  // pointer-events:none on --locked to make it bulletproof visually
  // and interactively.
  setCtaLocked(locked) {
    if (!this.addToCartBtn) return;
    this.addToCartBtn.disabled = !!locked;
    this.addToCartBtn.classList.toggle('bundle-cta__button--locked', !!locked);
  }

  setCtaIncomplete() {
    if (!this.addToCartBtn) return;
    this.setCtaLocked(true);
    const txt = this.addToCartBtn.dataset.textIncomplete;
    if (txt) this.addToCartBtn.textContent = txt;
  }

  setCtaReady(available) {
    if (!this.addToCartBtn) return;
    this.setCtaLocked(!available);
    const txt = this.addToCartBtn.dataset.textAdd;
    if (txt) this.addToCartBtn.textContent = txt;
  }

  // Atualiza, quando o bundle resolve numa variante valida: o price block
  // nativo do tema SE existir (normalmente desabilitado no bundle) e as linhas
  // de parcelamento + cashback dentro do CTA do combo. Cada ref pode ser null
  // (defensivo).
  updateExternalPriceElements(matched) {
    const price = matched.price; // cents
    const compareAt = matched.compare_at_price; // cents or null

    if (this.externalPriceEl) {
      this.externalPriceEl.textContent = this.formatter.format(price / 100);
    }
    if (this.externalCompareEl) {
      if (compareAt && compareAt > price) {
        this.externalCompareEl.textContent = this.formatter.format(compareAt / 100);
        this.externalCompareEl.hidden = false;
      } else {
        this.externalCompareEl.hidden = true;
      }
    }

    // Parcelamento: "Nx de R$ Y sem juros" — linha de apoio dentro do CTA.
    const installmentCount = parseInt(this.dataset.installmentCount, 10);
    if (this.installmentEl) {
      if (installmentCount > 1) {
        const each = price / installmentCount;
        this.installmentEl.textContent =
          `${installmentCount}x de ${this.formatter.format(each / 100)} sem juros`;
        this.installmentEl.hidden = false;
      } else {
        this.installmentEl.hidden = true;
      }
    }

    // Cashback: template localizado via data-cashback-format (i18n via locale).
    // Match Liquid logic em snippets/cashback.liquid: floor antes de dividir.
    const cashbackPct = parseFloat(this.dataset.cashbackPercentage);
    if (this.cashbackEl) {
      if (cashbackPct > 0) {
        const cashbackValue = Math.floor(price * (cashbackPct / 100));
        const formatted = this.formatter.format(cashbackValue / 100);
        const tpl = this.dataset.cashbackFormat || 'Receba {amount} de cashback';
        this.cashbackEl.textContent = tpl.replace('{amount}', formatted);
        this.cashbackEl.hidden = false;
      } else {
        this.cashbackEl.hidden = true;
      }
    }
  }

  resolveBundleVariant() {
    if (!this.productCache) return;
    // Modo de campanha: o aviso volta ao texto original (um erro do add nao fica grudado).
    this.restaurarAvisoKitAvulso();

    const optionValues = Object.values(this.selectedByComponent);

    // Incomplete: user hasn't selected one swatch per component.
    // Keep CTA in "Selecione um tom" state. No OOS alert (selection isn't
    // wrong — it just isn't complete yet).
    if (optionValues.length < this.expectedComponentCount) {
      this.setCtaIncomplete();
      if (this.unavailableEl) this.unavailableEl.hidden = true;
      return;
    }

    // Match tolerante a sufixo " + <extra>" em ambos os lados:
    //   option do componente "#100 Avelã" matches option do produto-pai "#100 Avelã + BioRetinol".
    // Convencao: o que vem antes de " + " e o tom/option canonico; o que vem depois sao
    // extras inclusos no kit (presentes no produto-pai pra aparecer no carrinho, ausentes
    // no produto componente que e vendido avulso). No-op em options sem " + " (ex: Combo
    // Base+BBS usa " / " separator do Shopify Bundles — split em ' + ' devolve full string).
    const stripExtra = (s) => s.split(' + ')[0].trim();
    let matched;
    if (this.dataset.bundleSlots) {
      // Modo slots: casa POR POSICAO. A opcao i da variante do kit e o tom do
      // card do slot i (data-component-index i+1, 1-based). Sem isso, com o
      // mesmo produto em dois slots, "#400 / #600 / Amora" e "#600 / #400 /
      // Amora" casariam igual.
      const slots = Number(this.dataset.bundleSlots);
      matched = this.productCache.variants.find((v) => {
        for (let i = 0; i < slots; i++) {
          const val = this.selectedByComponent[i + 1];
          const opt = v.options && v.options[i];
          if (val == null || opt == null || stripExtra(String(opt)) !== stripExtra(String(val))) return false;
        }
        return true;
      });
    } else {
      matched = this.productCache.variants.find((v) =>
        optionValues.every((val) =>
          v.options.some((opt) => stripExtra(opt) === stripExtra(val))
        )
      );
    }

    if (matched) {
      if (this.variantInput) this.variantInput.value = matched.id;
      if (this.totalEl) this.totalEl.textContent = this.formatter.format(matched.price / 100);
      if (this.compareEl) {
        if (matched.compare_at_price && matched.compare_at_price > matched.price) {
          this.compareEl.textContent = this.formatter.format(matched.compare_at_price / 100);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }
      // Discount badge: -XX% green pill. Mesma logica do snippet Liquid:
      // (compare_at_price - price) * 100 / compare_at_price, rounded.
      if (this.discountEl) {
        if (matched.compare_at_price && matched.compare_at_price > matched.price) {
          const diff = matched.compare_at_price - matched.price;
          const pct = Math.round(diff * 100 / matched.compare_at_price);
          this.discountEl.textContent = `-${pct}%`;
          this.discountEl.hidden = false;
        } else {
          this.discountEl.hidden = true;
        }
      }
      this.setCtaReady(matched.available);
      if (this.unavailableEl) this.unavailableEl.hidden = true;
      this.updateExternalPriceElements(matched);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', matched.id);
      window.history.replaceState({}, '', url.toString());

      if (this.kitAvulso) {
        const itens = this.kitAvulsoItens();
        if (itens) {
          this.mostrarContaKitAvulso(itens);
        } else {
          this.setCtaIncomplete();
        }
      }
    } else {
      // All selected, but no SKU for this combination.
      this.setCtaReady(false);
      if (this.unavailableEl) this.unavailableEl.hidden = false;
    }
  }
}

if (!customElements.get('bundle-selector')) {
  customElements.define('bundle-selector', BundleSelector);
}

// ---------------------------------------------------------------------------
// Fallback de add-to-cart pra page template (LP com bundle-selector normal).
//
// Na PDP o tema converte o form do bundle em AJAX (theme.applyAjaxToProductForm
// -> remove o action + abre o cart drawer), via attachThemeAjaxHandler() acima.
// Esse init do tema so roda em template de produto/grid: em page template (as
// LPs) NAO roda, entao o form faria submit NATIVO pro /cart/add e o Yampi
// jogaria o cliente direto pro checkout. Aqui replicamos o add nativo do tema
// (/cart/add.js + theme.updateCartSummaries) -- MESMO padrao do
// biobio-bundle-selector-trio.js, comprovado no live desde 15/06.
//
// Guard global proprio (window.__biobioBundlePageFallback): com 2 selectors na
// pagina o <script src> aparece 2x e o Shopify nao dedupa tag <script> (mesmo
// motivo do window.__biobioLpInit). O guard garante UM listener por documento.
//
// Coexistencia com o IIFE do trio: numa LP de trio os DOIS arquivos carregam
// (o trio estende esta classe) e cada IIFE tem guard distinto, entao AMBOS os
// listeners bindam no mesmo bundle-cta__form. Nao ha add duplicado porque a
// classe `product-add--adding` e setada SINCRONAMENTE antes do fetch: o
// primeiro listener a rodar a seta e o segundo sai cedo (submit no document
// dispara os listeners em sequencia sincrona no mesmo evento). DEFERE pro tema
// sempre que ele ja assumiu o form (product-form-loaded ou action removido) ->
// inerte na PDP.
(function () {
  if (window.__biobioBundlePageFallback) return;
  window.__biobioBundlePageFallback = true;

  function themeTookOver(form) {
    return form.classList.contains('product-form-loaded') || !form.getAttribute('action') || form.hasAttribute('data-kit-avulso');
  }

  document.addEventListener('submit', async function (event) {
    const form = event.target;
    if (!form || !form.classList || !form.classList.contains('bundle-cta__form')) return;
    if (themeTookOver(form)) return; // PDP: o tema cuida do AJAX (inerte aqui)

    event.preventDefault();

    const idInput = form.querySelector('[name="id"]');
    const variantId = idInput && idInput.value;
    const btn = form.querySelector('[data-bundle-add-to-cart], button[name="add"]');
    if (!variantId || (btn && btn.disabled)) return;
    // Anti duplo-clique E cross-guard com o IIFE do trio (ver nota acima).
    if (btn && btn.classList.contains('product-add--adding')) return;

    if (btn) btn.classList.add('product-add--adding');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || 'HTTP ' + res.status);
      }

      const t = window.theme;
      const cartPageMode = !!(t && t.settings && t.settings.cartType === 'page');
      if (t && typeof t.updateCartSummaries === 'function' && !cartPageMode) {
        // bundle pede o drawer sempre (wrapper data-open-cart-drawer); 3o arg
        // false evita a distorcao do drawer (mesma nota da guia e do trio).
        t.updateCartSummaries(true, true, false);
        if (btn) {
          btn.classList.add('product-add--added');
          setTimeout(function () { btn.classList.remove('product-add--added'); }, 2000);
        }
      } else {
        window.location.href = '/cart';
      }
    } catch (err) {
      // NUNCA redireciona pro checkout, sem alert: so re-habilita e avisa.
      console.warn('[bundle-selector] add-to-cart fallback falhou', err);
    } finally {
      if (btn) btn.classList.remove('product-add--adding');
    }
  }, false);
})();

// Galeria mais alta que a coluna de compra (desktop).
//
// O breakout de biobio-bundle-pdp.css joga as faixas de conteudo (Mais sobre
// cada formula, Como usar, stories, compre junto) pra largura total da .row
// logo depois do bloco de compra, contando que a galeria ja acabou ali. A
// galeria tem a altura da foto MAIS ALTA do produto: com foto 2:3 no kit
// (Trio Anti, desde 15/10/2026) ela passa do fim da coluna e as miniaturas
// ficam por cima da faixa. Aqui a primeira faixa ganha padding-top ate o
// conteudo dela comecar abaixo da galeria.
//
// So age quando JA ha sobreposicao: galeria que termina acima da faixa, por
// pouco que seja, fica intocada (varredura das 68 PDPs no ar em 03/11/2026,
// so o Trio Anti sobrepunha). Padding e nao margin porque margin colapsa com a
// do bloco anterior e o deslocamento sairia menor que o calculado.
(function () {
  if (window.__biobioBundleGaleriaAlta) return;
  window.__biobioBundleGaleriaAlta = true;

  const FOLGA = 32;
  const ATTR = 'data-galeria-folga';
  const desktop = window.matchMedia('(min-width: 768px)');

  // A faixa e o primeiro bloco da coluna que saiu pela esquerda dela e cruza
  // a faixa horizontal da galeria.
  function primeiraFaixa(detalhes, galeria) {
    const esquerda = detalhes.getBoundingClientRect().left;
    const g = galeria.getBoundingClientRect();
    for (let el = detalhes.firstElementChild; el; el = el.nextElementSibling) {
      const r = el.getBoundingClientRect();
      if (r.height > 0 && r.left < esquerda - 1 && r.left < g.right && r.right > g.left) return el;
    }
    return null;
  }

  function soltar(el) {
    el.style.paddingTop = '';
    el.removeAttribute(ATTR);
  }

  function ajustar() {
    const galeria = document.querySelector('.product-container .product-gallery');
    const detalhes = document.querySelector('.product-container .product-details');
    if (!galeria || !detalhes) return;

    const faixa = desktop.matches ? primeiraFaixa(detalhes, galeria) : null;
    const marcada = detalhes.querySelector(':scope > [' + ATTR + ']');
    if (marcada && marcada !== faixa) soltar(marcada);
    if (!faixa) return;

    const extra = parseFloat(faixa.getAttribute(ATTR)) || 0;
    const base = parseFloat(getComputedStyle(faixa).paddingTop) - extra;
    // Onde o conteudo da faixa comecaria sem a folga (o topo da caixa nao muda com padding).
    const topo = faixa.getBoundingClientRect().top + base;
    const sobra = galeria.getBoundingClientRect().bottom - topo;
    const novo = sobra > 0 ? Math.ceil(sobra + FOLGA) : 0;
    if (novo === extra) return;
    if (!novo) { soltar(faixa); return; }
    faixa.style.paddingTop = (base + novo) + 'px';
    faixa.setAttribute(ATTR, String(novo));
  }

  // rAF: o ajuste muda a altura da coluna observada; ajustar fora do callback
  // evita o aviso "ResizeObserver loop" e junta varias mudancas num quadro.
  let pendente = false;
  function agendar() {
    if (pendente) return;
    pendente = true;
    requestAnimationFrame(function () { pendente = false; ajustar(); });
  }

  function iniciar() {
    const galeria = document.querySelector('.product-container .product-gallery');
    const detalhes = document.querySelector('.product-container .product-details');
    if (!galeria || !detalhes || !document.querySelector('bundle-selector, bundle-selector-trio')) return;
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(agendar);
      ro.observe(galeria);
      ro.observe(detalhes);
    }
    window.addEventListener('load', agendar);
    if (desktop.addEventListener) desktop.addEventListener('change', agendar);
    agendar();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
  else iniciar();
})();
